import { cn } from "@wags/ui/ui-utils";
import { VercelImage } from "@wags/ui/vercel-image.tsx";
import {
    animate,
    motion,
    useMotionValue,
    useReducedMotion,
} from "framer-motion";
import { useRef, useState } from "react";

export type BreedSectionData = {
    dogName?: string;
    photos?: string[];
    exercise?: string;
    coat?: string;
    trainability?: string;
    lifespan?: string;
    livingSpace?: string;
};

function compact<T>(items: Array<T | false | 0 | "" | null | undefined>): T[] {
    return items.filter((item): item is T => Boolean(item));
}

const POLAROID_SLOTS = {
    front: { x: "-56%", y: 24, rotate: -4, scale: 1 },
    back: { x: "-38%", y: 4, rotate: 7, scale: 0.96 },
} as const;

const POLAROID_DRAG = {
    limit: { x: 72, y: 56 },
    distance: 36,
    flickDistance: 8,
    velocity: 400,
    lift: { scale: 1.025 },
    spring: { type: "spring", stiffness: 380, damping: 30 },
} as const;

function resistDrag(value: string | number, limit: number) {
    return limit * Math.tanh(Number.parseFloat(String(value)) / limit);
}

function PolaroidCard({
    src,
    dogName,
    isFront,
    canShuffle,
    onShuffle,
}: {
    src: string;
    dogName: string;
    isFront: boolean;
    canShuffle: boolean;
    onShuffle: () => void;
}) {
    const x = useMotionValue(0);
    const y = useMotionValue(0);
    const dragged = useRef(false);
    const reducedMotion = useReducedMotion();
    const transition = reducedMotion ? { duration: 0 } : POLAROID_DRAG.spring;

    return (
        <motion.div
            initial={false}
            animate={isFront ? POLAROID_SLOTS.front : POLAROID_SLOTS.back}
            transition={transition}
            className={cn(
                "absolute top-0 left-1/2 w-[218px]",
                isFront ? "z-10" : "z-0",
            )}
        >
            <motion.button
                type="button"
                tabIndex={canShuffle ? 0 : -1}
                aria-label={
                    canShuffle
                        ? isFront
                            ? `Photo of ${dogName} (activate to shuffle)`
                            : "Bring this photo to the front"
                        : `Photo of ${dogName}`
                }
                style={{ x, y, touchAction: canShuffle ? "none" : "auto" }}
                onPointerDown={() => {
                    dragged.current = false;
                }}
                onClick={(event) => {
                    if (
                        canShuffle &&
                        (event.detail === 0 || !dragged.current)
                    ) {
                        onShuffle();
                    }
                }}
                drag={isFront && canShuffle}
                transformTemplate={({ x = 0, y = 0, scale = 1 }) => {
                    const resistedX = resistDrag(x, POLAROID_DRAG.limit.x);
                    const resistedY = resistDrag(y, POLAROID_DRAG.limit.y);
                    return `translate3d(${resistedX}px, ${resistedY}px, 0) scale(${scale})`;
                }}
                dragMomentum={false}
                whileDrag={reducedMotion ? undefined : POLAROID_DRAG.lift}
                transition={transition}
                onDragStart={() => {
                    dragged.current = true;
                }}
                onDragEnd={(event, info) => {
                    const distance = Math.hypot(info.offset.x, info.offset.y);
                    const velocity = Math.hypot(
                        info.velocity.x,
                        info.velocity.y,
                    );
                    if (
                        event.type !== "pointercancel" &&
                        (distance >= POLAROID_DRAG.distance ||
                            (distance >= POLAROID_DRAG.flickDistance &&
                                velocity >= POLAROID_DRAG.velocity))
                    ) {
                        onShuffle();
                    }
                    animate(x, 0, transition);
                    animate(y, 0, transition);
                }}
                className={cn(
                    "border-shadow w-full select-none rounded-[6px] bg-true-white p-2 pb-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-labrador-yellow",
                    canShuffle &&
                        (isFront
                            ? "cursor-grab active:cursor-grabbing"
                            : "cursor-pointer"),
                )}
            >
                <div className="img-inset pointer-events-none aspect-square overflow-hidden rounded-[3px] bg-havanese">
                    <VercelImage
                        listingMedia
                        src={src}
                        alt={dogName}
                        width={440}
                        height={440}
                        draggable={false}
                        className="h-full w-full object-cover"
                    />
                </div>
            </motion.button>
        </motion.div>
    );
}

export function BreedSection({ data }: { data: BreedSectionData }) {
    const stackPhotos = compact([data.photos?.[0], data.photos?.[1]]);
    const [frontIdx, setFrontIdx] = useState(0);
    const swap = () => setFrontIdx((i) => (i === 0 ? 1 : 0));
    const rows = compact<[string, string]>([
        data.exercise ? ["Exercise", data.exercise] : undefined,
        data.coat ? ["Coat", data.coat] : undefined,
        data.trainability ? ["Trainability", data.trainability] : undefined,
        data.lifespan ? ["Lifespan", data.lifespan] : undefined,
        data.livingSpace ? ["Living space", data.livingSpace] : undefined,
    ]);

    return (
        <div>
            <h2 className="text-section-title text-doberman-black">
                Know the breed
            </h2>
            {stackPhotos.length > 0 && (
                <div className="relative mx-auto mt-4 h-[300px] w-full max-w-[300px]">
                    {stackPhotos.map((src, i) => (
                        <PolaroidCard
                            // oxlint-disable-next-line react/no-array-index-key -- slot position is the stable identity here (2 photos swapping places), not list order
                            key={i}
                            src={src}
                            dogName={data.dogName ?? "this dog"}
                            isFront={i === frontIdx}
                            canShuffle={stackPhotos.length > 1}
                            onShuffle={swap}
                        />
                    ))}
                </div>
            )}
            {rows.length > 0 && (
                <div className="mt-4 flex flex-col">
                    {rows.map(([label, value]) => (
                        <div
                            key={label}
                            className="flex items-center justify-between gap-4 border-b border-doberman-black/8 py-3 last:border-b-0"
                        >
                            <span className="text-body text-doberman-black/55">
                                {label}
                            </span>
                            <span className="text-right text-body font-medium text-doberman-black">
                                {value}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
